/**
 * @module unifiedScrollManager
 * @description Unified Scroll Manager - Coordinates sticky header and mobile banner behavior
 * Manages scroll state across multiple components to prevent conflicts
 */
window.unifiedScrollManager = {
  // Configuration
  scrollThreshold: 100,        // Min distance before showing/hiding
  debounceTime: 150,          // Debounce for scroll events
  nearBottomDistance: 150,    // Distance from bottom to be considered "near"
  minToggleInterval: 300,     // Min time between show/hide toggles
  
  // State
  lastScrollTop: 0,
  lastToggleTime: 0,
  direction: 'none',
  isNearBottom: false,
  isPastThreshold: false,
  scrollHandler: null,
  isInitialized: false,
  
  /**
   * Updates the current scroll state
   * @returns {{scrollTop: number, direction: string, isNearBottom: boolean, isPastThreshold: boolean, canToggle: boolean}} Current scroll state object
   */
  updateState: function() {
    const st = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;
    const distanceFromBottom = scrollHeight - (st + clientHeight);
    
    // Update direction
    if (st > this.lastScrollTop + 10) {
      this.direction = 'down';
    } else if (st < this.lastScrollTop - 10) {
      this.direction = 'up';
    }
    
    // Update position flags
    this.isNearBottom = distanceFromBottom < this.nearBottomDistance;
    this.isPastThreshold = st > this.scrollThreshold;
    this.lastScrollTop = st;
    
    return {
      scrollTop: st,
      direction: this.direction,
      isNearBottom: this.isNearBottom,
      isPastThreshold: this.isPastThreshold,
      canToggle: (Date.now() - this.lastToggleTime) > this.minToggleInterval
    };
  },
  
  /**
   * Records toggle action to prevent rapid state changes
   * @param {string} component - Name of the component being toggled
   * @param {string} action - Action performed ('show' or 'hide')
   * @fires toggle - Custom event with component and action details
   */
  recordToggle: function(component, action) {
    this.lastToggleTime = Date.now();
    
    // Dispatch toggle event for debugging/monitoring
    const event = new CustomEvent('componentToggle', {
      detail: {
        component: component,
        action: action,
        timestamp: this.lastToggleTime
      },
      bubbles: true
    });
    window.dispatchEvent(event);
  },
  
  // Throttle function
  throttle: function(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  },
  
  // Initialize unified scroll handling
  init: function() {
    if (this.isInitialized) return;
    
    // Track component visibility state
    this.componentStates = {
      stickyHeader: false,
      mobileBanner: false
    };
    
    // Create single throttled scroll handler
    this.scrollHandler = this.throttle(() => {
      const state = this.updateState();
      
      // Dispatch custom event with scroll state
      const event = new CustomEvent('unifiedScroll', {
        detail: state,
        bubbles: true
      });
      window.dispatchEvent(event);
    }, this.debounceTime);
    
    // Add single scroll listener
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    
    // Set up Intersection Observer for CSS safety net
    const mainHeader = document.querySelector('main-header.site-header');
    if (mainHeader) {
      const headerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            // When header is NOT intersecting (out of view), add class
            document.body.classList.toggle('scrolled-past-header', !entry.isIntersecting);
          });
        },
        { 
          threshold: 0,
          rootMargin: '-50px 0px 0px 0px' // Trigger 50px before header is fully out of view
        }
      );
      headerObserver.observe(mainHeader);
    }
    
    this.isInitialized = true;
  },
  
  // Update component state and record if changed
  updateComponentState: function(component, isVisible) {
    const previousState = this.componentStates[component];
    if (previousState !== isVisible) {
      this.componentStates[component] = isVisible;
      this.recordToggle(component, isVisible ? 'show' : 'hide');
      return true; // State changed
    }
    return false; // No change
  },
  
  // Sync scroll position - useful when components need accurate position after idle
  syncScrollPosition: function() {
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    this.lastScrollTop = currentScrollTop;
    this.direction = 'none'; // Reset direction after sync
    // console.log('[UnifiedScrollManager] Synced scroll position:', currentScrollTop);
  },
  
  // Clean up
  destroy: function() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
      this.isInitialized = false;
    }
  }
};

class SwiperConfigurator {
  constructor(sliderElement) {
    this.element = sliderElement;
    this.options = this.parseConfig();
    this.setupEventListeners();

    if (this.options.observer) {
      this.initIntersectionObserver();
    } else {
      this.initializeSwiper();
    }
  }

  parseConfig() {
    const {
      dataset: { config, mobile, observer }
    } = this.element;

    try {
      return {
        config: JSON.parse(config),
        mobile: mobile === 'true',
        observer: observer === 'true'
      };
    } catch (error) {
      console.error('Error parsing slider configuration:', error);
      return { config: {}, mobile: false, observer: false };
    }
  }

  initializeSwiper() {
    if (this.shouldDisableSwiper()) {
      return;
    }

    if (!Swiper || !this.element || this.element.swiper) {
      return;
    }

    new Swiper(this.element, this.options.config);
  }

  shouldDisableSwiper() {
    return this.options.mobile && window.innerWidth > 768;
  }

  setupEventListeners() {
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  handleResize() {
    if (this.shouldDisableSwiper()) {
      this.destroySwiper();
    } else {
      this.initializeSwiper();
    }
  }

  destroySwiper() {
    if (this.element && this.element.swiper) {
      this.element.swiper.destroy(true, true);
    }
  }

  initIntersectionObserver() {
    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // console.log('Element is in view, initializing swiper');
            this.observer.disconnect();
            this.initializeSwiper();
          }
        });
      },
      { threshold: 0.1 }
    );

    this.observer.observe(this.element);
  }

  static appendTagToBody(tagType, filePath) {
    return new Promise((resolve, reject) => {
      const tag = document.createElement(tagType === 'script' ? 'script' : 'link');

      if (tagType === 'script') {
        tag.src = filePath;
        tag.type = 'text/javascript';
      } else if (tagType === 'style') {
        tag.href = filePath;
        tag.rel = 'stylesheet';
      } else {
        return reject(new Error('Unsupported tag type. Please use "script" or "style".'));
      }

      tag.onload = resolve;
      tag.onerror = () => reject(new Error(`Failed to load ${tagType} file: ${filePath}`));

      document.body.appendChild(tag);
    });
  }

  static appendSwiperFiles() {
    const { css, js } = window.swiperFile;
    const files = [
      { type: 'style', path: css },
      { type: 'script', path: js }
    ].filter(file => file.path);

    if (files.length === 0) {
      return Promise.resolve();
    }

    const loadFilePromises = files.map(file => SwiperConfigurator.appendTagToBody(file.type, file.path));
    return Promise.all(loadFilePromises);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const sliderElements = document.querySelectorAll('[data-slider-selector]');
  if (sliderElements.length > 0) {
    SwiperConfigurator.appendSwiperFiles()
      .then(() => {
        sliderElements.forEach(slider => new SwiperConfigurator(slider));
      })
      .catch(error => {
        console.error('Failed to load Swiper files:', error);
      });
  }
});
